import { useEffect, useRef } from 'react';

export interface UseGlobalSearchShortcutOptions {
  /**
   * When false the listener is still attached but never fires
   * ``onActivate`` and never prevents the native find. Lets a caller
   * disable the app-wide shortcut (e.g. in a context where the browser
   * find is genuinely wanted) without conditionally calling the hook.
   * Defaults to true.
   */
  enabled?: boolean;
}

/**
 * #1392 — app-wide Cmd/Ctrl+F handler.
 *
 * Captures the browser "find" chord (⌘F on macOS, Ctrl+F on Win/Linux),
 * suppresses the native in-page find dialog, and invokes ``onActivate``
 * so the app can focus its own search box instead. Isolated, parallel-
 * safe: a single document-level keydown listener with no dependency on
 * the gutschrift-check canvas internals.
 *
 * The callback is held in a ref so a caller can pass an inline arrow
 * without churning the listener on every render.
 */
export function useGlobalSearchShortcut(
  onActivate: () => void,
  options: UseGlobalSearchShortcutOptions = {},
): void {
  const { enabled = true } = options;
  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // The find chord requires exactly one of Cmd / Ctrl. Shift is
      // allowed (Cmd+Shift+F still means "find" to the user); Alt is
      // not part of the chord but we don't reject it — only the F key
      // plus a primary modifier matters.
      const hasPrimaryModifier = event.metaKey || event.ctrlKey;
      if (!hasPrimaryModifier) return;
      if (event.key.toLowerCase() !== 'f') return;

      event.preventDefault();
      onActivateRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);
}

export default useGlobalSearchShortcut;
